import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import api from '../api';
import { toast } from 'sonner';

// Logs the user out after `idleMs` of no pointer/keyboard activity.
// Defaults to 30 minutes — matches typical corporate session policies and
// stays well within the 15-minute access-token lifetime so we don't keep
// silently refreshing an unattended session.
export function useIdleLogout({ idleMs = 30 * 60 * 1000 } = {}) {
  const user = useAuthStore(s => s.user);
  const touch = useAuthStore(s => s.touch);
  const clear = useAuthStore(s => s.clear);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;

    const logOutDueToIdle = async () => {
      try { await api.post('/auth/logout'); } catch (_) { /* ignore */ }
      clear();
      toast.info('You have been signed out due to inactivity.');
      if (window.location.pathname.startsWith('/')) {
        window.location.href = '/login';
      }
    };

    const reset = () => {
      touch();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logOutDueToIdle, idleMs);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, idleMs, touch, clear]);
}
